import { Loading } from '@nextui-org/react';
import React, { useId } from 'react';
import styled, { css } from 'styled-components';

import { lightTheme } from '../../theme/Themes';
import Ripple from './Ripple';

enum ButtonRoleEnum {
  Cancel,
  Destructive,
  Primary,
}

export class ButtonRole {
  static Cancel: ButtonRole = new ButtonRole(ButtonRoleEnum.Cancel);
  static Destructive: ButtonRole = new ButtonRole(ButtonRoleEnum.Destructive);
  static Primary: ButtonRole = new ButtonRole(ButtonRoleEnum.Primary);

  private buttonRole: ButtonRoleEnum;

  constructor(buttonRole: ButtonRoleEnum) {
    this.buttonRole = buttonRole;
  }

  backgroundColor(theme = lightTheme): string {
    switch (this.buttonRole) {
      case ButtonRoleEnum.Destructive:
        return theme.colors.system.error.c600;
      case ButtonRoleEnum.Cancel:
        return 'transparent';
      default:
        return theme.colors.primary.c400;
    }
  }

  backgroundColorHover = (theme = lightTheme): string => {
    switch (this.buttonRole) {
      case ButtonRoleEnum.Destructive:
        return theme.colors.system.error.c500;
      case ButtonRoleEnum.Cancel:
        return theme.colors.neutrals.white;
      default:
        return theme.colors.primary.c600;
    }
  };

  backgroundColorActive = (theme = lightTheme): string => {
    switch (this.buttonRole) {
      case ButtonRoleEnum.Destructive:
        return theme.colors.system.error.c700;
      case ButtonRoleEnum.Cancel:
        return theme.colors.neutrals.c100;
      default:
        return theme.colors.primary.c700;
    }
  };

  colorHover = (theme = lightTheme): string => {
    switch (this.buttonRole) {
      case ButtonRoleEnum.Destructive:
        return theme.colors.neutrals.white;
      case ButtonRoleEnum.Cancel:
        return theme.colors.neutrals.black;
      default:
        return theme.colors.neutrals.black;
    }
  };

  colorActive = (theme = lightTheme): string => {
    switch (this.buttonRole) {
      case ButtonRoleEnum.Destructive:
        return theme.colors.neutrals.white;
      case ButtonRoleEnum.Cancel:
        return theme.colors.neutrals.black;
      default:
        return theme.colors.neutrals.black;
    }
  };

  color = (theme = lightTheme): string => {
    switch (this.buttonRole) {
      case ButtonRoleEnum.Destructive:
        return theme.colors.neutrals.white;
      case ButtonRoleEnum.Cancel:
        return theme.colors.neutrals.white;
      default:
        return theme.colors.neutrals.black;
    }
  };

  borderColor = (theme = lightTheme): string => {
    switch (this.buttonRole) {
      case ButtonRoleEnum.Destructive:
        return 'transparent';
      case ButtonRoleEnum.Cancel:
        return theme.colors.neutrals.white;
      default:
        return 'transparent';
    }
  };
}

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  loading?: boolean;
  loadingSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  $disableMinWidth?: boolean;
  $width?: string;
  $height?: string;

  // If you really need a custom color prop, don't collide with HTML "color"
  $color?: string;

  $mobileRemoveBorder?: boolean;
  $buttonRole?: unknown;
  $noBorder?: boolean;

  children?: React.ReactNode;
  css?: any;
}

const StyledLoading = styled(Loading)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
`;

export const ButtonStyled = styled.button<ButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};
  border-radius: 0;
  border: none;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  outline: none;
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontVariants.desktop.body.md.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  height: 2.5rem;
  min-width: ${({ $disableMinWidth }) => ($disableMinWidth ? undefined : '6.125rem')};
  width: ${({ $width }) => $width || 'unset'};
  color: ${({ loading, color, theme }) => (loading ? 'transparent' : color || theme.colors.neutrals.black)};
  gap: 1.25rem;
  background: ${({ theme }) => theme.colors.primary.c500};
  min-width: ${({ $disableMinWidth }) => $disableMinWidth && '0px !important;'};
  padding: 0 0.625rem;

  svg {
    stroke: ${({ theme }) => theme.colors.neutrals.black};
  }

  :hover {
    background-color: ${({ theme }) => theme.colors.primary.c600};
  }

  & > span {
    z-index: 4;
    pointer-events: none;
    display: flex;
    align-items: center;
  }
  .nextui-loading {
    --nextui--loadingColor: ${({ theme }) => theme.colors.neutrals.c500};
  }
  .nextui-button-icon {
    svg {
      stroke: ${({ theme }) => theme.colors.neutrals.black};
    }
    margin-right: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: ${({ $width }) => $width || '100%'};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background: ${({ theme }) => theme.colors.neutrals.c600};
      opacity: 0.7;
      color: ${({ theme }) => theme.colors.neutrals.white};
      cursor: not-allowed;
      :hover {
        background-color: ${({ theme }) => theme.colors.neutrals.c600};
      }
    `}
`;

const Button = ({ children, loading, loadingSize = 'sm', disabled, ...rest }: ButtonProps) => {
  const reactId = useId();
  const id = rest.id ?? `button-custom-${reactId}`;

  return (
    <ButtonStyled
      {...rest}
      id={id}
      disabled={disabled || loading} // optional: disable while loading
      type={rest.type ?? 'button'} // optional: default type
    >
      {!disabled && !loading && <Ripple randomId={reactId} />}
      {loading ? <StyledLoading size={loadingSize} /> : <span>{children}</span>}
    </ButtonStyled>
  );
};

export default Button;
